<template>
  <v-container fluid class="cotizador">
    <div class="d-flex align-center flex-wrap mb-4">
      <h2 class="text-h5 mr-3">{{ quoteId ? 'Editar borrador' : 'Nueva cotización' }}</h2>
      <v-spacer />
      <v-btn text @click="pricingOpen = true">Pawer Pricing</v-btn>
      <v-btn text @click="proposalsOpen = true">Partners activos</v-btn>
      <v-btn text to="/cotizaciones">Ver cotizaciones</v-btn>
    </div>

    <v-alert v-if="parametersError" type="warning" text>
      {{ parametersError }}
      <span v-if="canApprove">Cárgalos con «Actualizar parámetros», al final de la página.</span>
    </v-alert>

    <template v-if="parameters">
      <v-card outlined class="mb-4">
        <v-card-text class="pt-6 pb-2">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="client"
                :items="clientItems"
                :search-input.sync="clientSearch"
                :loading="searchingClients"
                item-text="name"
                item-value="id"
                label="Empresa (Pipedrive)"
                placeholder="Escribe el nombre"
                no-filter
                return-object
                clearable
                dense
                outlined
              >
                <template #no-data>
                  <v-list-item v-if="canCreateClient" @click="createClientOpen = true">
                    <v-list-item-content>
                      <v-list-item-title>Crear «{{ clientSearch.trim() }}» en Pipedrive</v-list-item-title>
                      <v-list-item-subtitle>No está en Pipedrive todavía</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-else>
                    <v-list-item-title class="text-caption">Escribe al menos 2 letras</v-list-item-title>
                  </v-list-item>
                </template>
                <!-- Pipedrive matches by similarity ("Star Chile" finds "AuguStar Seguros Chile"): the new company must still be creatable. -->
                <template #append-item>
                  <v-list-item v-if="canCreateClient && clientItems.length && !exactClientMatch" @click="createClientOpen = true">
                    <v-list-item-content>
                      <v-list-item-title>Crear «{{ clientSearch.trim() }}» en Pipedrive</v-list-item-title>
                      <v-list-item-subtitle>Si no es ninguna de las anteriores</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="6" md="3">
              <v-select
                v-model="form.countryIso"
                :items="countries"
                label="País"
                :disabled="countryFromPipedrive"
                :hint="countryHint"
                persistent-hint
                dense
                outlined
              />
            </v-col>
            <v-col cols="6" md="3">
              <v-select v-model="form.line" :items="lines" label="Línea" dense outlined @change="onLineChange" />
            </v-col>
            <v-col v-if="form.pipedriveOrgId" cols="12" md="6">
              <v-select
                :value="dealSelection"
                :items="dealItems"
                :loading="loadingDeals"
                label="Trato (opcional)"
                hint="La nota de la propuesta queda en este trato, o en la empresa si no eliges uno."
                persistent-hint
                clearable
                dense
                outlined
                @change="onDealChange"
              />
            </v-col>
            <v-col v-if="form.pipedriveOrgId && form.newDeal" cols="12" md="6">
              <v-select
                v-model="form.pipedriveNewDealPipelineId"
                :items="pipelines"
                :loading="loadingPipelines"
                item-text="name"
                item-value="id"
                label="Embudo"
                hint="El trato se crea en la etapa «Propuesta Enviada» al generar la propuesta."
                persistent-hint
                dense
                outlined
              />
            </v-col>

            <v-col cols="6" md="3">
              <v-select v-model="form.channel" :items="channelOptions" label="Canal" dense outlined />
            </v-col>
            <v-col v-if="form.channel === 'broker'" cols="6" md="3">
              <v-select
                v-model="form.brokerId"
                :items="brokers"
                item-text="name"
                item-value="id"
                label="Broker"
                dense
                outlined
                @change="onBrokerChange"
              />
            </v-col>
            <v-col v-if="showCommission" cols="6" md="3">
              <v-text-field
                v-model.number="form.commissionPct"
                type="number"
                min="0"
                max="50"
                suffix="%"
                :label="commissionLabel"
                dense
                outlined
              />
            </v-col>
            <v-col v-if="isAssistance" cols="6" md="3">
              <v-select
                v-model="form.assistanceProduct"
                :items="petProductOptions"
                label="¿Es un producto de mascotas?"
                dense
                outlined
              />
            </v-col>
            <v-col v-if="isAssistance" cols="6" md="3">
              <v-select v-model="form.modality" :items="modalityOptions" label="Modalidad" dense outlined />
            </v-col>
            <v-col v-if="!isAssistance" cols="6" md="3">
              <v-text-field
                v-model.number="form.quantity"
                type="number"
                min="1"
                label="Total de colaboradores"
                dense
                outlined
              />
            </v-col>
            <v-col v-if="!isAssistance" cols="6" md="3">
              <v-text-field
                v-model.number="form.membershipsPerEmployee"
                type="number"
                min="1"
                max="5"
                label="Membresías por colaborador"
                :hint="membershipsHint"
                persistent-hint
                dense
                outlined
              />
            </v-col>
            <template v-else>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model.number="form.stock"
                  type="number"
                  min="0"
                  label="Stock (pólizas activas)"
                  dense
                  outlined
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-text-field
                  v-model.number="form.monthlyNewSales"
                  type="number"
                  min="0"
                  label="Venta nueva mensual"
                  :hint="projectionHint"
                  persistent-hint
                  dense
                  outlined
                />
              </v-col>
            </template>
          </v-row>
        </v-card-text>
      </v-card>

      <v-alert v-if="unsupportedCountry" type="warning" text dense>
        {{ client.name }} es de {{ clientCountry.countryLabel }} en Pipedrive: ese país todavía no se cotiza.
      </v-alert>

      <v-alert v-if="onlyVirtual" type="info" text dense>
        Una alianza solo Virtual se cotiza solo con el plan Virtual.
      </v-alert>
      <v-alert v-if="underMinimum" type="info" text dense>
        Wellness y Urgencias se venden desde {{ minWithCoverage }} colaboradores.
      </v-alert>

      <v-progress-linear v-if="calculating" indeterminate class="mb-2" />
      <v-alert v-if="previewError" type="error" text>{{ previewError }}</v-alert>
      <p v-else-if="!preview && !underMinimum" class="text-body-2">Ingresa la cantidad para ver los precios.</p>

      <template v-if="preview && !previewError">
        <v-alert v-if="preview.warnings.includes('EXCHANGE_RATE_STALE')" type="warning" dense text class="mb-3">
          El tipo de cambio tiene más de 30 días.
        </v-alert>

        <div class="d-flex align-center flex-wrap mb-1 cotizador__section-title">
          <h3 class="text-h6 mr-4">{{ proposalTitle }}</h3>
          <v-text-field
            v-model.number="form.discountPct"
            type="number"
            min="0"
            max="99"
            suffix="%"
            label="Descuento"
            dense
            outlined
            hide-details
            class="cotizador__small-input"
          />
        </div>
        <div class="text-body-2 mb-4">
          Valores Mensuales
          <span v-if="preview.commissionPct" class="text-caption">
            · comisión del broker {{ pct(preview.commissionPct) }} aparte
          </span>
        </div>

        <v-expansion-panels v-model="coveragesPanel" flat class="mb-6 cotizador__coverages">
          <v-expansion-panel>
            <v-expansion-panel-header class="px-4">
              <div class="d-flex align-center">
                <span class="text-subtitle-1 font-weight-bold">Coberturas de los planes</span>
                <v-spacer />
                <v-btn
                  small
                  text
                  color="primary"
                  class="mr-2"
                  :disabled="!canAddCustomPlan"
                  @click.stop="openCustomizer(null)"
                >
                  <v-icon small left>mdi-plus</v-icon>
                  Plan a medida
                </v-btn>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <plans-comparison :lines="collectiveLines" :services="catalog" :currency="preview.currency">
                <template #plan-actions="{ line }">
                  <div v-if="line.customKey" class="mt-1">
                    <v-btn x-small text color="primary" class="px-1" @click="openCustomizer(line.customKey)">
                      Editar
                    </v-btn>
                    <v-btn x-small text class="px-1" @click="removeCustomPlan(line.customKey)">Quitar</v-btn>
                  </div>
                </template>
              </plans-comparison>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="d-flex align-center mb-2">
          <h4 class="text-subtitle-1 font-weight-bold">{{ isAssistance ? 'Planes' : 'Planes Colectivos' }}</h4>
          <v-spacer />
          <div v-if="canShowUf" class="d-flex align-center">
            <span v-if="showUf" class="text-caption mr-3">UF {{ ufLabel }}</span>
            <v-switch v-model="showUf" label="Ver en UF" dense hide-details class="mt-0 pt-0" data-test="uf-switch" />
          </div>
        </div>

        <quote-result
          :uf-value="showUf && canShowUf ? preview.uf.value : null"
          :lines="collectiveLines"
          :currency="preview.currency"
          :commission-pct="preview.commissionPct"
          :is-assistance="isAssistance"
          :minimum-usd="minimumUsd"
          :market-references="marketReferences"
          :show-meta="false"
        />

        <template v-if="!isAssistance">
          <div class="d-flex align-center flex-wrap mt-6 mb-2 cotizador__section-title">
            <h4 class="text-subtitle-1 font-weight-bold">Planes Flexibles</h4>
            <span class="cotizador__separator">|</span>
            <span class="text-subtitle-1">Bolsa</span>
            <v-text-field
              :value="form.poolSize"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="cotizador__inline-input"
              @input="onPoolSizeInput"
            />
            <span class="text-subtitle-1">Colaboradores</span>
            <span class="cotizador__separator">|</span>
            <v-text-field
              :value="form.poolPct"
              type="number"
              min="1"
              max="100"
              suffix="%"
              dense
              outlined
              hide-details
              class="cotizador__inline-input"
              @input="onPoolPctInput"
            />
            <v-spacer />
            <span v-if="preview.pool && preview.pool.participation < 0.25" class="text-caption">
              Bajo 25% de participación lleva recargo
            </span>
          </div>
          <p v-if="!poolLines.length" class="text-body-2">
            Virtual no se vende en bolsa, y Wellness y Urgencias necesitan una bolsa de al menos
            {{ minWithCoverage }} colaboradores.
          </p>
          <quote-result
            v-else
            :lines="poolLines"
            :currency="preview.currency"
              :commission-pct="preview.commissionPct"
            :minimum-usd="minimumUsd"
            :market-references="marketReferences"
            :show-meta="false"
          />
        </template>

        <v-textarea v-model="form.notes" label="Notas (opcional)" rows="2" auto-grow dense outlined class="mt-4" />

        <div class="d-flex mt-2">
          <span v-if="!form.clientName" class="text-caption align-self-center">Elige la empresa para poder guardar.</span>
          <v-spacer />
          <v-btn
            v-if="canWriteDraft"
            text
            :disabled="!canSave || generating"
            :loading="saving && !generating"
            @click="save(false)"
          >
            Guardar borrador
          </v-btn>
          <v-btn
            v-if="canGenerateProposal"
            color="primary"
            :disabled="!canSave || saving"
            :loading="generating"
            class="ml-2"
            @click="save(true)"
          >
            Generar Propuesta
          </v-btn>
        </div>
      </template>
    </template>

    <pricing-parameters-footer
      :version="parameters ? parameters.version : null"
      :can-refresh="canApprove"
      @refreshed="onParametersRefreshed"
      @error="showErrorSnackbar"
    />

    <plan-customizer-modal
      v-model="customizerOpen"
      :plan="editingCustomPlan"
      :plans="plansForLine(parameters && parameters.parameters, form.line)"
      :variants="variants.filter((v) => basePlans.includes(v.basePlan))"
      :base-plans="basePlans"
      :services="catalog"
      :can-save-variant="form.channel === 'broker' && Boolean(form.brokerId) && canCreateVariant"
      :pricer="canPriceLive ? priceCandidate : null"
      :currency="preview ? preview.currency : 'CLP'"
      :is-assistance="isAssistance"
      @apply="applyCustomPlan"
      @save-variant="saveVariant"
    />

    <pawer-pricing-modal v-model="pricingOpen" :minimum-usd="minimumUsd" />

    <partners-modal
      v-model="proposalsOpen"
      :standard-plans="plansForLine(parameters && parameters.parameters, 'employee_benefit')"
      :services="chileCatalog"
      :tiers="chileTiers"
      :flexible-factor="parameters ? parameters.parameters.rules.flexibleFactor : 1.25"
    />

    <v-dialog v-model="createClientOpen" max-width="440">
      <v-card>
        <v-card-title class="text-h6">Crear empresa en Pipedrive</v-card-title>
        <v-card-text>
          <p>
            Se creará la organización «{{ (clientSearch || '').trim() }}» en Pipedrive, visible para todo el equipo.
            Revisa que no exista con otro nombre.
          </p>
          <v-select v-model="form.countryIso" :items="countries" label="País" dense outlined hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="createClientOpen = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="creatingClient" @click="createClient">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" top>{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import QuoteResult from '../components/QuoteResult.vue';
import PricingParametersFooter from '../components/PricingParametersFooter.vue';
import PlansComparison from '../components/PlansComparison.vue';
import PlanCustomizerModal from '../components/PlanCustomizerModal.vue';
import PartnersModal from '../components/PartnersModal.vue';
import PawerPricingModal from '../components/PawerPricingModal.vue';
import B2bQuotesService from '../services/b2b-quotes.service';
import BrokerService from '../services/broker.service';
import { utilsMixin } from '../mixins/utilsMixin';
import {
  PET_PRODUCT_OPTIONS,
  CHANNEL_LABELS,
  COUNTRY_OPTIONS,
  LINE_OPTIONS,
  MODALITY_OPTIONS,
  PLAN_OPTIONS,
  NEW_DEAL,
  dealSelectItems,
  dealSelectionPatch,
  errorMessage,
  formatPct,
  plansForLine
} from '../utils/b2b-quotes.utils';
import { plansToQuote, recommendedPool, splitSavedLines, toQuoteBody, uniquePlanName } from '../utils/b2b-quote-form.utils';
import { can } from '../utils/permissions';

const PREVIEW_DEBOUNCE_MS = 400;
/** The Flexible offer starts from this share of the employees. */
const DEFAULT_POOL_PCT = 30;
const CLIENT_SEARCH_DEBOUNCE_MS = 300;
/** The backend prices up to 10 plans per offer: the 3 standard ones plus the custom ones. */
const MAX_CUSTOM_PLANS = 5;
let customPlanSeq = 0;

/**
 * Standard plans quoted in a line, in display order. Virtual is not quoted in
 * Employee Benefit (it goes in the deck as a no-brainer); Pro only exists in
 * Asistencias, once the parameters have it.
 */
function basePlansOf(line, parameters = null) {
  const sold = plansForLine(parameters, line).map((p) => p.code);
  return PLAN_OPTIONS.map((p) => p.value).filter((plan) => {
    if (plan === 'virtual') return line === 'asistencias';
    if (plan === 'pro') return line === 'asistencias' && sold.includes('pro');
    return true;
  });
}

function standardPlans(line = 'employee_benefit', parameters = null) {
  return basePlansOf(line, parameters).map((basePlan) => ({ basePlan, partnerVariantId: null, changes: [] }));
}

function emptyForm() {
  return {
    pipedriveOrgId: null,
    pipedriveDealId: null,
    /** "+ Crear trato nuevo" chosen: the proposal creates the deal in `pipedriveNewDealPipelineId`. */
    newDeal: false,
    pipedriveNewDealPipelineId: null,
    clientName: '',
    countryIso: 'CL',
    line: 'employee_benefit',
    channel: 'direct',
    brokerId: null,
    brokerName: null,
    modality: 'collective',
    assistanceProduct: null,
    quantity: null,
    stock: null,
    monthlyNewSales: null,
    poolPct: DEFAULT_POOL_PCT,
    membershipsPerEmployee: 1,
    poolSize: null,
    commissionPct: null,
    discountPct: 0,
    notes: '',
    lines: standardPlans(),
    customPlans: []
  };
}

export default {
  name: 'Cotizador',
  components: { QuoteResult, PricingParametersFooter, PlanCustomizerModal, PlansComparison, PartnersModal, PawerPricingModal },
  mixins: [utilsMixin],
  data() {
    return {
      parameters: null,
      parametersError: '',
      canApprove: false,
      marketReferences: [],
      form: emptyForm(),
      preview: null,
      /** Asistencias in Chile: show the plans' prices in UF instead of pesos. */
      showUf: false,
      previewError: '',
      calculating: false,
      saving: false,
      generating: false,
      deals: [],
      loadingDeals: false,
      pipelines: [],
      loadingPipelines: false,
      previewTimer: null,
      previewSeq: 0,
      variants: [],
      brokers: [],
      client: null,
      clientCountry: null,
      clientItems: [],
      clientSearch: null,
      clientTimer: null,
      searchingClients: false,
      createClientOpen: false,
      poolSizeTyped: false,
      creatingClient: false,
      customizerOpen: false,
      /** Key of the custom plan being edited; null while creating one. */
      customizerKey: null,
      coveragesPanel: null,
      proposalsOpen: false,
      pricingOpen: false,
      previewKeys: [],
      countries: COUNTRY_OPTIONS,
      lines: LINE_OPTIONS,
      petProductOptions: PET_PRODUCT_OPTIONS,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    quoteId() {
      return this.$route.params.id || null;
    },
    isAssistance() {
      return this.form.line === 'asistencias';
    },
    /** What the market references depend on: country, line and, in Asistencias, the kind of product. */
    marketReferenceKey() {
      const product = this.isAssistance ? this.form.assistanceProduct : '';
      return `${this.form.countryIso}|${this.form.line}|${product}`;
    },
    /** Asistencias quoted in pesos with a UF from the backend (pawer-admin spec 037). */
    canShowUf() {
      return this.isAssistance && this.preview?.currency === 'CLP' && Boolean(this.preview?.uf?.value);
    },
    ufLabel() {
      const { value, date } = this.preview.uf;
      const [year, month, day] = date.split('-');
      const pesos = value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `$${pesos} al ${day}-${month}-${year}`;
    },
    channelOptions() {
      const channels = this.parameters?.parameters.channels ?? [];
      // Both lines sell direct or through a broker (an insurer can come through a corredor).
      return channels
        .filter((c) => c.code !== 'assistance')
        .map((c) => ({ value: c.code, text: CHANNEL_LABELS[c.code] ?? c.code }));
    },
    currentChannel() {
      return this.parameters?.parameters.channels.find((c) => c.code === this.form.channel) ?? null;
    },
    showCommission() {
      return this.form.channel !== 'direct';
    },
    commissionLabel() {
      const preset = this.currentChannel?.defaultCommission;
      if (preset === null || preset === undefined) return 'Comisión (0% si se deja vacía)';
      return `Comisión (por defecto ${Math.round(preset * 100)}%)`;
    },
    modalityOptions() {
      return MODALITY_OPTIONS[this.form.line];
    },
    /** Asistencias are priced for the insured expected in 12 months: stock + 12 × new sales. */
    projectedInsured() {
      const stock = Number(this.form.stock || 0);
      const monthly = Number(this.form.monthlyNewSales || 0);
      return stock + 12 * monthly;
    },
    projectionHint() {
      if (!this.projectedInsured) return 'Por mes';
      return `A 12 meses: ${this.projectedInsured.toLocaleString('es-CL')} asegurados`;
    },
    /** Memberships the collective offer prices: employees, or insured in 12 months in Asistencias. */
    pricedQuantity() {
      return this.isAssistance ? this.projectedInsured : this.form.quantity;
    },
    proposalTitle() {
      const n = Number(this.pricedQuantity || 0).toLocaleString('es-CL');
      return this.isAssistance
        ? `Propuesta para ${n} asegurados a 12 meses`
        : `Propuesta para ${n} colaboradores`;
    },
    /** Collective plans of the preview; a custom plan carries its key, in the order it was sent. */
    collectiveLines() {
      return (this.preview?.lines ?? [])
        .filter((l) => l.modality !== 'pool')
        .map((l, index) => ({ ...l, customKey: this.previewKeys[index] ?? null }));
    },
    poolLines() {
      return (this.preview?.lines ?? []).filter((l) => l.modality === 'pool');
    },
    hasPool() {
      return this.poolLines.length > 0;
    },
    catalog() {
      const services = this.parameters?.parameters.services ?? [];
      return services
        .filter((s) => s.country === this.form.countryIso && s.kind === 'reimbursement')
        .map((s) => ({ code: s.code, name: s.name, cap: s.cap }));
    },
    /** A plan pays at least one block of 1.000 memberships of the digital floor a month. */
    minimumUsd() {
      return `USD ${this.parameters?.parameters.rules.digitalFloorUsdPer1000 ?? 99}`;
    },
    /** Partner proposals are Chilean, so they compare against the Chilean catalog. */
    chileCatalog() {
      const services = this.parameters?.parameters.services ?? [];
      return services
        .filter((s) => s.country === 'CL' && s.kind === 'reimbursement')
        .map((s) => ({ code: s.code, name: s.name, cap: s.cap }));
    },
    chileTiers() {
      return this.parameters?.parameters.priceLists.find((l) => l.country === 'CL')?.tiers ?? [];
    },
    /** The customizer can price live once the quote has something to price. */
    canPriceLive() {
      if (!this.pricedQuantity || this.pricedQuantity < 1 || this.underMinimum) return false;
      return !(this.form.channel === 'broker' && !this.form.brokerId);
    },
    membershipsHint() {
      const total = Number(this.form.quantity || 0) * Number(this.form.membershipsPerEmployee || 1);
      return total ? `Colectivo: ${total.toLocaleString('es-CL')} membresías, activen o no` : '';
    },
    minWithCoverage() {
      return this.parameters?.parameters.rules.minMembershipsWithCoverage ?? 25;
    },
    onlyVirtual() {
      return this.isAssistance && this.form.assistanceProduct === 'virtual_alliance';
    },
    /** Employee Benefit sells only coverage plans, so under the minimum there is nothing to quote. */
    underMinimum() {
      return !this.isAssistance && this.pricedQuantity > 0 && this.pricedQuantity < this.minWithCoverage;
    },
    basePlans() {
      return basePlansOf(this.form.line, this.parameters && this.parameters.parameters);
    },
    /** The quote's country comes from Pipedrive whenever the organization has one. */
    countryFromPipedrive() {
      return Boolean(this.clientCountry?.countryLabel);
    },
    unsupportedCountry() {
      return this.countryFromPipedrive && !COUNTRY_OPTIONS.some((c) => c.value === this.clientCountry.countryIso);
    },
    countryHint() {
      if (!this.client) return '';
      if (this.countryFromPipedrive) return 'Desde Pipedrive';
      return 'Sin país en Pipedrive: se guardará al guardar la cotización';
    },
    canCreateClient() {
      return (this.clientSearch || '').trim().length >= 2 && !this.searchingClients && can('quote-builder:create');
    },
    /** Saving a draft creates it on `/cotizador` and updates it on `/cotizador/:id` (spec 132). */
    canWriteDraft() {
      return can(this.quoteId ? 'quote-builder:update' : 'quote-builder:create');
    },
    /** Generating a proposal saves the draft first, so it needs both tags. */
    canGenerateProposal() {
      return this.canWriteDraft && can('quote-builder:generate-proposal');
    },
    canCreateVariant() {
      return can('quote-builder:create');
    },
    /** A found company with exactly the typed name (ignoring case and spaces): nothing to create then. */
    exactClientMatch() {
      const typed = (this.clientSearch || '').trim().toLocaleLowerCase('es');
      return this.clientItems.some((item) => (item.name || '').trim().toLocaleLowerCase('es') === typed);
    },
    canSave() {
      return Boolean(this.preview) && !this.previewError && Boolean(this.form.clientName) && !this.unsupportedCountry;
    },
    editingCustomPlan() {
      return this.form.customPlans.find((p) => p.key === this.customizerKey) ?? null;
    },
    dealItems() {
      return dealSelectItems(this.deals);
    },
    dealSelection() {
      return this.form.newDeal ? NEW_DEAL : this.form.pipedriveDealId;
    },
    canAddCustomPlan() {
      return this.form.customPlans.length < MAX_CUSTOM_PLANS;
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.schedulePreview();
      }
    },
    marketReferenceKey: {
      immediate: true,
      handler() {
        this.loadMarketReferences();
      }
    },
    'form.quantity'(employees) {
      // The pool follows the employees by its percentage until the executive types a size.
      if (!this.poolSizeTyped) this.form.poolSize = recommendedPool(employees, this.form.poolPct);
    },
    'form.channel'(channel, previous) {
      if (previous === undefined) return;
      if (channel !== 'broker') {
        this.form.brokerId = null;
        this.form.brokerName = null;
      }
      this.form.commissionPct = null;
      this.loadVariants();
    },
    client(value) {
      // A deal belongs to its organization: choosing another one clears it.
      if (this.form.pipedriveOrgId !== (value?.id ?? null)) Object.assign(this.form, dealSelectionPatch(null));
      this.form.pipedriveOrgId = value?.id ?? null;
      this.form.clientName = value?.name ?? '';
      this.loadClientCountry(value?.id ?? null);
    },
    'form.pipedriveOrgId'(pipedriveOrgId) {
      this.loadDeals(pipedriveOrgId);
    },
    clientSearch(value) {
      clearTimeout(this.clientTimer);
      this.clientTimer = setTimeout(() => this.searchClients(value), CLIENT_SEARCH_DEBOUNCE_MS);
    }
  },
  async created() {
    await Promise.all([this.loadParameters(), this.loadBrokers()]);
    if (this.quoteId) await this.loadDraft();
    this.loadVariants();
  },
  beforeDestroy() {
    clearTimeout(this.previewTimer);
    clearTimeout(this.clientTimer);
  },
  methods: {
    plansForLine,
    async loadParameters() {
      const res = await B2bQuotesService.getActiveParameters();
      if (res.status === 'ok') {
        this.parameters = res.data;
        this.canApprove = res.data.canApprove;
        this.parametersError = '';
      } else {
        this.parametersError = errorMessage(res.code);
        // Nothing imported yet: only an approver can fix it, and the button needs to show.
        if (res.code === 'PRICING_PARAMETERS_NOT_LOADED') this.canApprove = can('pricing:approve');
      }
    },
    async onParametersRefreshed({ version, changed }) {
      this.showSuccessSnackbar(changed ? `Parámetros cargados: versión ${version}` : 'El Sheet no cambió: se mantiene la versión actual');
      if (!changed) return;
      await this.loadParameters();
      this.schedulePreview();
    },
    /** References are a hint next to the price: without them the quote works the same. */
    async loadMarketReferences() {
      const key = this.marketReferenceKey;
      const res = await B2bQuotesService.getMarketReferences(this.form.countryIso, {
        line: this.form.line,
        petProduct: this.form.assistanceProduct === 'pet_insurance'
      });
      // A slower answer for an earlier line or product must not overwrite the current one.
      if (key !== this.marketReferenceKey) return;
      this.marketReferences = res.status === 'ok' ? res.data : [];
    },
    async loadBrokers() {
      const res = await BrokerService.getAll();
      if (res.status === 'ok') this.brokers = res.data.map((b) => ({ id: b.id, name: b.name }));
    },
    async loadDraft() {
      const res = await B2bQuotesService.get(this.quoteId);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return;
      }
      const quote = res.data;
      if (quote.status !== 'draft') {
        this.$router.replace(`/cotizaciones/${quote.id}`);
        return;
      }
      const input = quote.input;
      if (quote.pipedriveOrgId) {
        this.clientItems = [{ id: quote.pipedriveOrgId, name: quote.clientName }];
      }
      this.form = {
        pipedriveOrgId: quote.pipedriveOrgId,
        pipedriveDealId: quote.pipedriveDealId ?? null,
        newDeal: Boolean(quote.pipedriveNewDealPipelineId),
        pipedriveNewDealPipelineId: quote.pipedriveNewDealPipelineId ?? null,
        clientName: quote.clientName,
        countryIso: input.countryIso,
        line: input.line,
        channel: input.channel,
        brokerId: quote.brokerId,
        brokerName: quote.brokerName,
        modality: input.modality,
        assistanceProduct: input.assistanceProduct,
        quantity: input.quantity,
        stock: input.stock ?? null,
        monthlyNewSales: input.monthlyNewSales ?? null,
        poolSize: input.poolSize ?? null,
        membershipsPerEmployee: input.membershipsPerEmployee ?? 1,
        poolPct: input.poolSize && input.quantity
          ? Math.round((input.poolSize / input.quantity) * 1000) / 10
          : DEFAULT_POOL_PCT,
        commissionPct: input.commissionPct === null ? null : input.commissionPct * 100,
        discountPct: Math.round(input.discountPct * 100),
        notes: quote.notes ?? '',
        ...this.linesFromSaved(input.lines, input.line)
      };
      this.poolSizeTyped = Boolean(input.poolSize);
      if (quote.pipedriveOrgId) this.client = this.clientItems[0];
    },
    async loadVariants() {
      if (!this.form.channel || (this.form.channel === 'broker' && !this.form.brokerId)) {
        this.variants = [];
        return;
      }
      const res = await B2bQuotesService.listPartnerVariants(this.form.channel, this.form.brokerId);
      this.variants = res.status === 'ok' ? res.data : [];
    },
    async searchClients(search) {
      const term = (search || '').trim();
      if (term.length < 2 || term === this.client?.name) return;
      this.searchingClients = true;
      const res = await B2bQuotesService.searchClients(term);
      this.searchingClients = false;
      if (res.status === 'ok') this.clientItems = res.data;
      else this.showErrorSnackbar(errorMessage(res.code));
    },
    /** Open deals of the organization; without them the proposal note goes to the organization. */
    async loadDeals(pipedriveOrgId) {
      this.deals = [];
      this.loadingDeals = Boolean(pipedriveOrgId);
      if (!pipedriveOrgId) return;
      const res = await B2bQuotesService.listDeals(pipedriveOrgId);
      // The executive may have picked another company meanwhile.
      if (this.form.pipedriveOrgId !== pipedriveOrgId) return;
      this.loadingDeals = false;
      if (res.status === 'ok') this.deals = res.data;
      else this.showErrorSnackbar(errorMessage(res.code));
    },
    onDealChange(value) {
      Object.assign(this.form, dealSelectionPatch(value));
      if (value === NEW_DEAL) this.loadPipelines();
    },
    /** Active Pipedrive pipelines, loaded once when "+ Crear trato nuevo" is first chosen. */
    async loadPipelines() {
      if (this.pipelines.length || this.loadingPipelines) return;
      this.loadingPipelines = true;
      const res = await B2bQuotesService.listPipelines();
      this.loadingPipelines = false;
      if (res.status === 'ok') this.pipelines = res.data;
      else this.showErrorSnackbar(errorMessage(res.code));
    },
    async loadClientCountry(pipedriveOrgId) {
      this.clientCountry = null;
      if (!pipedriveOrgId) return;
      const res = await B2bQuotesService.getClient(pipedriveOrgId);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return;
      }
      // An answer for a company the executive already replaced is ignored.
      if (this.form.pipedriveOrgId !== pipedriveOrgId) return;
      this.clientCountry = res.data;
      const supported = COUNTRY_OPTIONS.some((c) => c.value === res.data.countryIso);
      if (supported) this.form.countryIso = res.data.countryIso;
    },
    /** Fills the country in Pipedrive when the organization had none (before saving the quote). */
    async ensureClientCountry() {
      if (!this.form.pipedriveOrgId || !this.clientCountry || this.countryFromPipedrive) return true;
      const res = await B2bQuotesService.setClientCountry(this.form.pipedriveOrgId, this.form.countryIso);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return false;
      }
      this.clientCountry = res.data;
      return true;
    },
    async createClient() {
      this.creatingClient = true;
      const res = await B2bQuotesService.createClient(this.clientSearch.trim(), this.form.countryIso);
      this.creatingClient = false;
      this.createClientOpen = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return;
      }
      this.clientItems = [res.data];
      this.client = res.data;
      this.showSuccessSnackbar(`«${res.data.name}» creada en Pipedrive`);
    },
    onLineChange(line) {
      this.form.modality = MODALITY_OPTIONS[line][0].value;
      this.form.channel = 'direct';
      this.form.assistanceProduct = line === 'asistencias' ? 'other_insurance' : null;
      const parameters = this.parameters && this.parameters.parameters;
      this.form.lines = standardPlans(line, parameters);
      this.form.customPlans = this.form.customPlans.filter((p) => basePlansOf(line, parameters).includes(p.basePlan));
    },
    onBrokerChange(id) {
      this.form.brokerName = this.brokers.find((b) => b.id === id)?.name ?? null;
      this.loadVariants();
    },
    onPoolPctInput(value) {
      this.form.poolPct = value === '' ? null : Number(value);
      this.poolSizeTyped = false;
      this.form.poolSize = recommendedPool(this.form.quantity, this.form.poolPct);
    },
    onPoolSizeInput(value) {
      this.form.poolSize = value === '' ? null : Number(value);
      this.poolSizeTyped = true;
      if (this.form.quantity && this.form.poolSize) {
        this.form.poolPct = Math.round((this.form.poolSize / this.form.quantity) * 1000) / 10;
      }
    },
    pct(value) {
      return formatPct(value);
    },
    /** Saved lines as the form keeps them: the 3 standard plans, and custom plans apart. */
    linesFromSaved(saved, line) {
      const { standard, custom } = splitSavedLines(saved, standardPlans(line, this.parameters && this.parameters.parameters));
      return {
        lines: standard,
        customPlans: custom.map((line) => ({ ...line, key: `custom-${++customPlanSeq}` }))
      };
    },
    openCustomizer(key) {
      this.customizerKey = key;
      this.customizerOpen = true;
    },
    applyCustomPlan(plan) {
      const others = this.form.customPlans.filter((p) => p.key !== this.customizerKey);
      const taken = [...PLAN_OPTIONS.map((p) => p.text), ...others.map((p) => p.name)];
      const named = { ...plan, name: uniquePlanName(plan.name, taken) };
      if (this.editingCustomPlan) {
        Object.assign(this.editingCustomPlan, named);
      } else {
        this.form.customPlans.push({ ...named, key: `custom-${++customPlanSeq}` });
      }
    },
    /** Prices a plan being customized next to its base plan, with the quote's current inputs. */
    priceCandidate(plan) {
      const base = { basePlan: plan.basePlan, partnerVariantId: null, changes: [] };
      const candidate = { ...plan, name: plan.name || null };
      return B2bQuotesService.preview(toQuoteBody({ ...this.form, lines: [base, candidate] }, { pricingOnly: true }));
    },
    removeCustomPlan(key) {
      this.form.customPlans = this.form.customPlans.filter((p) => p.key !== key);
    },
    async saveVariant({ name, basePlan, changes }) {
      const res = await B2bQuotesService.createPartnerVariant({
        channel: this.form.channel,
        brokerId: this.form.brokerId,
        name,
        basePlan,
        changes
      });
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return;
      }
      this.showSuccessSnackbar(`Variante «${name}» guardada`);
      await this.loadVariants();
    },
    quoteForm() {
      const plans = [...this.form.lines, ...this.form.customPlans];
      return { ...this.form, lines: plansToQuote(plans, this.onlyVirtual) };
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(this.runPreview, PREVIEW_DEBOUNCE_MS);
    },
    async runPreview() {
      if (!this.parameters || !this.pricedQuantity || this.pricedQuantity < 1 || this.underMinimum) {
        this.preview = null;
        this.previewError = '';
        return;
      }
      if (this.form.channel === 'broker' && !this.form.brokerId) {
        this.preview = null;
        this.previewError = errorMessage('BROKER_REQUIRED');
        return;
      }
      const seq = ++this.previewSeq;
      this.calculating = true;
      const form = this.quoteForm();
      const res = await B2bQuotesService.preview(toQuoteBody(form, { pricingOnly: true }));
      // A slower, older answer must not overwrite a newer one.
      if (seq !== this.previewSeq) return;
      this.calculating = false;
      if (res.status === 'ok') {
        this.preview = res.data;
        this.previewKeys = form.lines.map((line) => line.key ?? null);
        this.previewError = '';
      } else {
        this.preview = null;
        this.previewError = errorMessage(res.code);
      }
    },
    /**
     * Saves the draft and, with `generate`, creates its Google Slides proposal
     * and sends it ("Generar Propuesta"). If the proposal fails the draft is
     * kept, so trying again updates it instead of creating another quote.
     */
    async save(generate) {
      this.saving = true;
      this.generating = generate;
      const saved = await this.saveDraft();
      if (!saved) {
        this.saving = false;
        this.generating = false;
        return;
      }
      if (!generate) {
        this.saving = false;
        this.showSuccessSnackbar('Borrador guardado');
        if (!this.quoteId) this.$router.replace(`/cotizador/${saved.id}`);
        return;
      }
      const res = await B2bQuotesService.generateProposal(saved.id);
      this.saving = false;
      this.generating = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        if (!this.quoteId) this.$router.replace(`/cotizador/${saved.id}`);
        return;
      }
      // The detail shows what failed in Pipedrive (the snackbar here would vanish).
      const warnings = res.meta?.warnings ?? [];
      this.$router.push({ path: `/cotizaciones/${saved.id}`, query: warnings.length ? { aviso: warnings.join(',') } : {} });
    },
    /** Creates or updates the draft; null (with the error shown) when it could not be saved. */
    async saveDraft() {
      if (!(await this.ensureClientCountry())) return null;
      const body = toQuoteBody(this.quoteForm());
      const res = this.quoteId
        ? await B2bQuotesService.updateDraft(this.quoteId, body)
        : await B2bQuotesService.create(body);
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res.code));
        return null;
      }
      return res.data;
    }
  }
};
</script>

<style scoped>
.cotizador__coverages {
  border: 1px solid var(--pw-border);
  border-radius: 8px;
  overflow: hidden;
}
/* The comparison table runs edge to edge under the header, with the cells' own padding. */
.cotizador__coverages ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
.cotizador__small-input {
  max-width: 150px;
}
.cotizador__section-title {
  gap: 8px;
}
.cotizador__inline-input {
  max-width: 110px;
}
.cotizador__separator {
  color: var(--pw-text-faint);
}
</style>
